import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
    ArrowLeft,
    Building,
    Globe,
    Mail,
    Phone,
    Download,
    ExternalLink,
    Shield,
    CheckCircle,
    XCircle
} from 'lucide-react';
import { louService, LOU } from '../services/louService';
import ReactCountryFlag from 'react-country-flag';

const LOUPage: React.FC = () => {
    const { louCode } = useParams<{ louCode: string }>();
    const [lou, setLOU] = useState<LOU | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchLOU = async () => {
            if (!louCode) {
                setError('No LOU code provided');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                const louData = await louService.findLOUByLOUCode(louCode);

                if (!louData) {
                    setError(`LOU with code ${louCode} not found`);
                } else {
                    setLOU(louData);
                }
            } catch (err) {
                setError('Failed to load LOU information');
                console.error('Error fetching LOU:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchLOU();
    }, [louCode]);

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 py-8">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="animate-pulse">
                        <div className="h-8 bg-gray-200 rounded-sm w-1/4 mb-6"></div>
                        <div className="bg-white rounded-lg shadow-xs border border-gray-200 p-6">
                            <div className="h-8 bg-gray-200 rounded-sm w-1/2 mb-4"></div>
                            <div className="space-y-3">
                                {[...Array(6)].map((_, i) => (
                                    <div key={i} className="h-4 bg-gray-200 rounded-sm w-3/4"></div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !lou) {
        return (
            <div className="min-h-screen bg-gray-50 py-8">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <Link
                        to="/search"
                        className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-800 transition-colors mb-6"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back to Search
                    </Link>

                    <div className="bg-white rounded-lg shadow-xs border border-gray-200 p-12 text-center">
                        <Building className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                        <h1 className="text-xl font-semibold text-gray-900 mb-2">LOU Not Found</h1>
                        <p className="text-gray-500 mb-6">
                            {error || 'The requested Local Operating Unit could not be found.'}
                        </p>
                        <Link
                            to="/search"
                            className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
                        >
                            Return to Search
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    const getStatusBadge = (status?: string) => {
        if (!status) return null;

        const isOperational = status === 'Operational';
        return (
            <span
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                    isOperational ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'
                }`}
            >
                {isOperational ? (
                    <CheckCircle className="h-3 w-3" />
                ) : (
                    <XCircle className="h-3 w-3" />
                )}
                {status}
            </span>
        );
    };

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Back Button */}
                <div className="mb-6">
                    <Link
                        to="/search"
                        className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-800 transition-colors"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back to Search
                    </Link>
                </div>

                {/* Page Header */}
                <div className="bg-white rounded-lg shadow-xs border border-gray-200 p-6 mb-6">
                    <div className="flex items-start gap-4">
                        <div className="bg-primary-600 p-3 rounded-lg">
                            <Building className="h-8 w-8 text-white" />
                        </div>
                        <div className="flex-1">
                            <h1 className="text-2xl font-bold text-gray-900 mb-2">{lou.name}</h1>
                            <div className="flex flex-wrap items-center gap-3 text-sm">
                                {lou.code && (
                                    <span className="font-mono bg-blue-100 px-2 py-1 rounded-sm text-blue-800">
                                        Code: {lou.code}
                                    </span>
                                )}
                                {getStatusBadge(lou.status)}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Main Information */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Basic Information */}
                        <div className="bg-white rounded-lg shadow-xs border border-gray-200 p-6">
                            <div className="flex items-center gap-3 mb-4">
                                <Building className="h-6 w-6 text-primary-600" />
                                <h2 className="text-xl font-semibold text-gray-900">
                                    Organization Details
                                </h2>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Top row: Country and Sponsor */}
                                <div>
                                    <p className="text-sm font-medium text-gray-500">Country</p>
                                    <p className="text-gray-900 uppercase flex items-center gap-2">
                                        <ReactCountryFlag
                                            countryCode={lou.sponsor_country}
                                            svg
                                            style={{
                                                width: '16px',
                                                height: '12px'
                                            }}
                                            title={lou.sponsor_country}
                                        />
                                        {lou.sponsor_country}
                                    </p>
                                </div>
                                {lou.sponsor && (
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">Sponsor</p>
                                        <p className="text-gray-900">{lou.sponsor}</p>
                                    </div>
                                )}
                                {/* Second row: LEI and LEI Prefix */}
                                {lou.lei && (
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">LEI</p>
                                        <p className="text-gray-900 font-mono">{lou.lei}</p>
                                    </div>
                                )}
                                <div>
                                    <p className="text-sm font-medium text-gray-500">LEI Prefix</p>
                                    <p className="text-gray-900 font-mono">
                                        {lou.prefix.replace(/['"]/g, '')}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Data Access Information */}
                        <div className="bg-white rounded-lg shadow-xs border border-gray-200 p-6">
                            <div className="flex items-center gap-3 mb-4">
                                <Shield className="h-6 w-6 text-primary-600" />
                                <h2 className="text-xl font-semibold text-gray-900">
                                    Data Access & Licensing
                                </h2>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <p className="text-sm font-medium text-gray-500">
                                        IP License Free
                                    </p>
                                    <p className="text-gray-900">
                                        {(() => {
                                            const value = lou.explicitly_ip_free
                                                ?.replace(/['"]/g, '')
                                                .trim();
                                            if (value === 'yes') return 'Yes';
                                            if (value === 'no') return 'No';
                                            return 'Not specified';
                                        })()}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-gray-500">
                                        Open Download
                                    </p>
                                    <p className="text-gray-900">
                                        {(() => {
                                            const value = lou.open_download
                                                ?.replace(/['"]/g, '')
                                                .trim();
                                            if (value === 'yes') return 'Yes';
                                            if (value === 'no') return 'No';
                                            return 'Not specified';
                                        })()}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* About LOUs */}
                        <div className="bg-blue-50 rounded-lg border border-blue-200 p-6">
                            <h3 className="text-lg font-semibold text-blue-900 mb-3">
                                About Local Operating Units
                            </h3>
                            <p className="text-blue-800 text-sm leading-relaxed">
                                Local Operating Units (LOUs) are organizations certified by the
                                Global Legal Entity Identifier Foundation (GLEIF) to issue Legal
                                Entity Identifiers (LEIs) within specific jurisdictions.
                            </p>
                        </div>
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">
                        {/* Contact Information */}
                        <div className="bg-white rounded-lg shadow-xs border border-gray-200 p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">
                                Contact Information
                            </h3>
                            <div className="space-y-3">
                                {lou.website && (
                                    <a
                                        href={lou.website}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 text-primary-600 hover:text-primary-800 transition-colors"
                                    >
                                        <Globe className="h-4 w-4" />
                                        Website
                                    </a>
                                )}
                                {lou.email && (
                                    <a
                                        href={`mailto:${lou.email}`}
                                        className="flex items-center gap-2 text-primary-600 hover:text-primary-800 transition-colors"
                                    >
                                        <Mail className="h-4 w-4" />
                                        {lou.email}
                                    </a>
                                )}
                                {lou.telephone && (
                                    <a
                                        href={`tel:${lou.telephone}`}
                                        className="flex items-center gap-2 text-primary-600 hover:text-primary-800 transition-colors"
                                    >
                                        <Phone className="h-4 w-4" />
                                        {lou.telephone}
                                    </a>
                                )}
                            </div>
                        </div>

                        {/* Resources */}
                        {(lou.download_url || lou.ip_licence_url) && (
                            <div className="bg-white rounded-lg shadow-xs border border-gray-200 p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                                    Resources
                                </h3>
                                <div className="space-y-3">
                                    {lou.download_url && (
                                        <a
                                            href={lou.download_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-2 text-primary-600 hover:text-primary-800 transition-colors"
                                        >
                                            <Download className="h-4 w-4" />
                                            Download Data
                                        </a>
                                    )}
                                    {lou.ip_licence_url && (
                                        <a
                                            href={lou.ip_licence_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-2 text-primary-600 hover:text-primary-800 transition-colors"
                                        >
                                            <ExternalLink className="h-4 w-4" />
                                            License Terms
                                        </a>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LOUPage;
